import React, { useEffect, useState, useCallback } from 'react';
import {
  GraduationCap, Users, Briefcase, Building2, Search, ShieldCheck, Lock,
} from 'lucide-react';
import api from '../api';
import './FeatureFlagPanel.css';

/**
 * InstitutionFeaturePanel v2 — role-aware feature matrix (master admin).
 *
 * Hierarchy: Institution → Role (student / faculty / tpo / university)
 *            → features → sub-features, every level toggleable.
 *
 * Backend endpoints:
 *   GET  /features/matrix                          — full matrix snapshot
 *   PUT  /features/institution/{id}                — institution-wide master switches + AI budget
 *   PUT  /features/institution/{id}/role/{role}    — role-level feature switches
 *   PUT  /features/institution/{id}/sub            — one sub-feature switch for a role
 *
 * Semantics: role flags only NARROW access — effective = institution AND role.
 * Disabling institution-wide also cascades to dependent features (ai_agents →
 * ai_analysis, proctoring) server-side.
 */

const ROLES = [
  { key: 'student',   label: 'Students',        icon: GraduationCap },
  { key: 'faculty',   label: 'Faculty',         icon: Users },
  { key: 'tpo',       label: 'TPO / Placement', icon: Briefcase },
  { key: 'university',label: 'University Admin',icon: Building2 },
];

// Master-switch labels (institution-wide section)
const MasterLabels = {
  ai_agents: 'AI Agents (Interview / Tutor / Counsellor)',
  sidebar_interviewer: 'AI Interviewer (Student Sidebar)',
  ai_analysis: 'AI Analysis (Resume & Reports)',
  ai_student_reports: 'AI Student Reports',
  lms: 'LMS (Courses, Marks, Attendance)',
  vetting: 'Vetting & Compliance',
  discussions: 'Discussions & Groups',
  certificates: 'Certificates',
  jobs: 'Jobs & Placement Drives',
  proctoring: 'Proctoring',
  mantrika: 'Mantrika Vendor Integration',
  navigatelabs: 'NavigateLabs SSO Integration',
  messages: 'Messages (Chat)',
  schedule: 'My Schedule (Calendar)',
  platform_feedback: 'Platform Feedback',
  internal_jobs: 'Campus Jobs (Internal TPO Postings)',
};

// Mirrors backend FEATURE_REGISTRY `requires`
const DEPENDENTS = { ai_agents: ['ai_analysis', 'ai_student_reports'] };

// Humanize a sub key when the backend catalogue has no description for it
const humanizeSub = (key) => key.replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const InstitutionFeaturePanel = () => {
  const [institutions, setInstitutions] = useState([]);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [activeRole, setActiveRole] = useState({});   // uni_id -> role key
  const [showMaster, setShowMaster] = useState({});   // uni_id -> bool
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);          // "${uniId}:${key}" tokens
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/features/matrix');
      setInstitutions(res.data?.institutions || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load the feature matrix');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(''), 5000); };
  const isSaving = (tok) => saving === tok;
  const setSavingTok = (tok) => setSaving(tok);
  const roleOf = (uniId) => activeRole[uniId] || 'student';

  const patchInstitution = (uniId, fn) => setInstitutions((list) => list.map((i) => (
    i.university_id === uniId ? fn(i) : i
  )));

  // ---------------------------------------------------------------- role toggle
  const toggleRoleFeature = async (inst, roleKey, featureKey, enabled) => {
    const roleSpec = inst.roles?.[roleKey]?.[featureKey];
    // If the institution-wide master is OFF, offer to flip both.
    if (enabled && roleSpec?.institution_enabled === false) {
      const ok = window.confirm(
        `"${MasterLabels[featureKey] || featureKey}" is OFF for the whole institution.\n\n` +
        'Enable it institution-wide AND for this role?'
      );
      if (!ok) return;
      try {
        setSavingTok(`${inst.university_id}:${featureKey}`);
        const resInst = await api.put(`/features/institution/${inst.university_id}`, {
          flags: { [featureKey]: true },
        });
        const res = await api.put(`/features/institution/${inst.university_id}/role/${roleKey}`, {
          flags: { [featureKey]: true },
        });
        // Apply the full returned flag sets so cascaded dependents stay fresh.
        const instEff = resInst.data.flags || {};
        const eff = res.data.flags || {};
        patchInstitution(inst.university_id, (i) => ({
          ...i,
          institution_flags: { ...i.institution_flags, ...instEff },
          roles: {
            ...i.roles,
            [roleKey]: Object.fromEntries(Object.entries(i.roles[roleKey] || {}).map(([fk, spec]) => {
              const next = { ...spec };
              if (eff[fk] !== undefined) next.enabled = !!eff[fk];
              if (instEff[fk] !== undefined) next.institution_enabled = !!instEff[fk];
              return [fk, next];
            })),
          },
        }));
        flash(`${MasterLabels[featureKey] || featureKey} enabled institution-wide and for ${roleKey}s`);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Update failed');
      } finally {
        setSaving(null);
      }
      return;
    }

    // Disabling a feature also disables the features that require it (the
    // backend cascades the effective flags) -- make that explicit here.
    if (!enabled) {
      const deps = (DEPENDENTS[featureKey] || [])
        .filter((d) => inst.roles?.[roleKey]?.[d] && inst.roles[roleKey][d].enabled !== false)
        .map((d) => inst.roles?.[roleKey]?.[d]?.label || MasterLabels[d] || d);
      if (deps.length) {
        const ok = window.confirm(
          `Disabling "${roleSpec?.label || MasterLabels[featureKey] || featureKey}" for ${roleKey}s ` +
          `will also disable: ${deps.join(', ')}.\n\nContinue?`
        );
        if (!ok) return;
      }
    }

    setSavingTok(`${inst.university_id}:${featureKey}`);
    setError('');
    try {
      const res = await api.put(`/features/institution/${inst.university_id}/role/${roleKey}`, {
        flags: { [featureKey]: enabled },
      });
      // The response carries the full effective flag set for this role
      // (requires-chain cascade included) -- apply every entry so dependent
      // feature cards do not go stale in the UI.
      const eff = res.data.flags || {};
      patchInstitution(inst.university_id, (i) => ({
        ...i,
        roles: {
          ...i.roles,
          [roleKey]: Object.fromEntries(Object.entries(i.roles[roleKey] || {}).map(([fk, spec]) => {
            const next = { ...spec };
            if (eff[fk] !== undefined) next.enabled = !!eff[fk];
            return [fk, next];
          })),
        },
      }));
      flash(`${roleSpec?.label || featureKey} ${enabled ? 'enabled' : 'disabled'} for ${roleKey}s at ${inst.institution_name}`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(null);
    }
  };

  // ---------------------------------------------------------------- sub toggle
  const toggleSub = async (inst, roleKey, featureKey, subKey, enabled) => {
    setSavingTok(`${inst.university_id}:${featureKey}:${subKey}`);
    setError('');
    try {
      const res = await api.put(`/features/institution/${inst.university_id}/sub`, {
        role_key: roleKey, feature_key: featureKey, sub_key: subKey, enabled,
      });
      patchInstitution(inst.university_id, (i) => ({
        ...i,
        roles: {
          ...i.roles,
          [roleKey]: {
            ...i.roles[roleKey],
            [featureKey]: { ...i.roles[roleKey][featureKey], subs: res.data.subs || {} },
          },
        },
      }));
      flash(`${subKey} ${enabled ? 'enabled' : 'disabled'} for ${roleKey}s`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(null);
    }
  };

  // ------------------------------------------------- institution-wide masters
  const toggleMaster = async (inst, key, enabled) => {
    const deps = !enabled ? (DEPENDENTS[key] || []) : [];
    if (deps.length) {
      const depNames = deps.map((d) => MasterLabels[d] || d).join(', ');
      const ok = window.confirm(
        `Disabling "${MasterLabels[key]}" institution-wide will also disable: ${depNames} for every role.\n\nContinue?`
      );
      if (!ok) return;
    }
    setSavingTok(`${inst.university_id}:master:${key}`);
    setError('');
    try {
      const res = await api.put(`/features/institution/${inst.university_id}`, {
        flags: { [key]: enabled },
      });
      patchInstitution(inst.university_id, (i) => ({
        ...i,
        institution_flags: { ...i.institution_flags, ...res.data.flags },
        // reflect effective result in every role view (role flags only narrow)
        roles: Object.fromEntries(Object.entries(i.roles || {}).map(([rk, feats]) => [
          rk,
          Object.fromEntries(Object.entries(feats).map(([fk, spec]) => [
            fk,
            res.data.flags?.[fk] === false
              ? { ...spec, enabled: false, institution_enabled: false }
              : (fk === key ? { ...spec, institution_enabled: enabled } : spec),
          ])),
        ])),
      }));
      flash(deps.length
        ? `${MasterLabels[key]} (and ${deps.length} dependent feature${deps.length > 1 ? 's' : ''}) disabled institution-wide`
        : `${MasterLabels[key]} ${enabled ? 'enabled' : 'disabled'} institution-wide`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(null);
    }
  };

  const setBudget = async (inst, value) => {
    setSavingTok(`${inst.university_id}:budget`);
    setError('');
    try {
      const res = await api.put(`/features/institution/${inst.university_id}`, {
        monthly_token_budget: value === '' ? null : parseInt(value, 10),
      });
      patchInstitution(inst.university_id, (i) => ({
        ...i, monthly_token_budget: res.data.monthly_token_budget,
      }));
      flash(`AI budget saved for ${inst.institution_name}`);
      setTimeout(() => setNotice(''), 4000);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Budget update failed');
    } finally {
      setSaving(null);
    }
  };

  // ---------------------------------------------------------------- rendering
  if (loading) return <div className="ffp-loading">Loading institution feature matrix…</div>;

  const q = search.trim().toLowerCase();
  const visible = institutions.filter((i) =>
    !q || (i.institution_name || '').toLowerCase().includes(q) || String(i.university_id).includes(q)
  );

  const countDisabled = (inst) => {
    const set = new Set();
    Object.values(inst.roles || {}).forEach((feats) => Object.entries(feats).forEach(([fk, s]) => {
      if (s.enabled === false) set.add(`${inst.university_id}:${fk}`);
    }));
    return set.size;
  };

  return (
    <div className="ffp-wrap ffp-wrap-wide">
      <div className="ffp-header">
        <h2>Institution Features</h2>
        <p>
          Pick an institution, choose a role, and control exactly which features and
          sub-features that role gets. Role switches narrow the institution-wide
          master switches — a role can never exceed the institution&apos;s entitlements.
        </p>
      </div>

      <div className="ffp-search">
        <Search size={15} />
        <input
          type="text"
          placeholder="Search institutions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <div className="ffp-banner ffp-error">{error}</div>}
      {notice && <div className="ffp-banner ffp-ok">{notice}</div>}

      <div className="ffp-list">
        {visible.map((inst) => {
          const isOpen = expanded === inst.university_id;
          const rKey = roleOf(inst.university_id);
          const roleMeta = ROLES.find((r) => r.key === rKey);
          const feats = inst.roles?.[rKey] || {};
          const disabledCount = countDisabled(inst);
          const budgetPct = inst.monthly_token_budget
            ? Math.min(100, Math.round((inst.tokens_used_this_month / inst.monthly_token_budget) * 100))
            : null;
          const masterOpen = showMaster[inst.university_id];

          return (
            <div key={inst.university_id} className={`ffp-card ${isOpen ? 'open' : ''}`}>
              <button className="ffp-card-head" onClick={() => setExpanded(isOpen ? null : inst.university_id)}>
                <div>
                  <span className="ffp-inst-name">{inst.institution_name || `Institution #${inst.university_id}`}</span>
                  <span className="ffp-inst-sub">
                    {disabledCount === 0 ? 'All role features enabled' : `${disabledCount} role feature(s) disabled`}
                    {inst.monthly_token_budget
                      ? ` · AI budget ${inst.tokens_used_this_month?.toLocaleString()} / ${inst.monthly_token_budget.toLocaleString()} tokens (${budgetPct}%)`
                      : ' · no AI budget set'}
                  </span>
                  {budgetPct !== null && (
                    <span className={`ffp-budgetbar ${budgetPct >= 100 ? 'over' : budgetPct >= 80 ? 'warn' : ''}`}>
                      <span style={{ width: `${budgetPct}%` }} />
                    </span>
                  )}
                </div>
                <span className="ffp-caret">{isOpen ? '▲' : '▼'}</span>
              </button>

              {isOpen && (
                <div className="ffp-card-body">
                  {/* -------- role tabs -------- */}
                  <div className="ffp-tabs">
                    {ROLES.map(({ key, label, icon: Icon }) => (
                      <button
                        key={key}
                        className={`ffp-tab ${rKey === key ? 'active' : ''}`}
                        onClick={() => setActiveRole((m) => ({ ...m, [inst.university_id]: key }))}
                      >
                        <Icon size={14} />
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>

                  {/* -------- role feature matrix -------- */}
                  <div className="ffp-matrix">
                    {Object.keys(feats).length === 0 && (
                      <div className="ffp-empty">
                        No role-specific features configured for {roleMeta?.label}.
                      </div>
                    )}
                    {Object.entries(feats).map(([fKey, spec]) => (
                      <div key={fKey} className={`ffp-feature-card ${spec.enabled ? '' : 'disabled'}`}>
                        <div className="ffp-feature-head">
                          <div className="ffp-feature-title">
                            <span className="ffp-label">{spec.label || MasterLabels[fKey] || fKey}</span>
                            {spec.institution_enabled === false && (
                              <span className="ffp-badge ffp-badge-warn" title="Institution-wide master switch is OFF">
                                <Lock size={11} /> institution OFF
                              </span>
                            )}
                            {spec.institution_enabled !== false && spec.enabled && (
                              <span className="ffp-badge ffp-badge-ok"><ShieldCheck size={11} /> active</span>
                            )}
                          </div>
                          <button
                            className={`ffp-switch ${spec.enabled ? 'on' : 'off'}`}
                            disabled={isSaving(`${inst.university_id}:${fKey}`)}
                            onClick={() => toggleRoleFeature(inst, rKey, fKey, !spec.enabled)}
                            aria-label={`${spec.enabled ? 'Disable' : 'Enable'} ${spec.label || fKey} for ${rKey}s`}
                          >
                            <span className="ffp-knob" />
                            <span className="ffp-state">{spec.enabled ? 'ON' : 'OFF'}</span>
                          </button>
                        </div>

                        {spec.subs && Object.keys(spec.subs).length > 0 && (
                          <div className="ffp-subs">
                            {Object.entries(spec.subs).map(([sKey, sEnabled]) => (
                              <div key={sKey} className={`ffp-sub-row ${sEnabled ? '' : 'sub-off'}`}>
                                <div className="ffp-sub-text">
                                  <span className="ffp-sub-label">{spec.sub_labels?.[sKey] || humanizeSub(sKey)}</span>
                                  <span className="ffp-sub-key">{sKey}</span>
                                </div>
                                <button
                                  className={`ffp-switch ffp-switch-sm ${sEnabled ? 'on' : 'off'}`}
                                  disabled={isSaving(`${inst.university_id}:${fKey}:${sKey}`) || spec.enabled === false}
                                  onClick={() => toggleSub(inst, rKey, fKey, sKey, !sEnabled)}
                                  title={spec.enabled === false ? 'Enable the feature first' : undefined}
                                  aria-label={`${sEnabled ? 'Disable' : 'Enable'} ${spec.sub_labels?.[sKey] || sKey} for ${rKey}s`}
                                >
                                  <span className="ffp-knob" />
                                  <span className="ffp-state">{sEnabled ? 'ON' : 'OFF'}</span>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* -------- institution-wide master switches -------- */}
                  <div className="ffp-master">
                    <button className="ffp-master-toggle" onClick={() => setShowMaster((m) => ({ ...m, [inst.university_id]: !masterOpen }))}>
                      {masterOpen ? '▾' : '▸'} Institution-wide master switches
                      <small> — apply to every role</small>
                    </button>
                    {masterOpen && (
                      <div className="ffp-master-body">
                        {Object.entries(MasterLabels).map(([key, label]) => (
                          <div key={key} className="ffp-row">
                            <span className="ffp-label">{label}</span>
                            <button
                              className={`ffp-switch ${inst.institution_flags?.[key] ? 'on' : 'off'}`}
                              disabled={isSaving(`${inst.university_id}:master:${key}`)}
                              onClick={() => toggleMaster(inst, key, !inst.institution_flags?.[key])}
                              aria-label={`${inst.institution_flags?.[key] ? 'Disable' : 'Enable'} ${label} institution-wide`}
                            >
                              <span className="ffp-knob" />
                              <span className="ffp-state">{inst.institution_flags?.[key] ? 'ON' : 'OFF'}</span>
                            </button>
                          </div>
                        ))}

                        <div className="ffp-row ffp-budget-row">
                          <span className="ffp-label">Monthly AI token budget</span>
                          <div className="ffp-budget-input">
                            <input
                              type="number"
                              min="0"
                              placeholder="No limit"
                              defaultValue={inst.monthly_token_budget ?? ''}
                              onBlur={(e) => {
                                const v = e.target.value;
                                const current = inst.monthly_token_budget ?? '';
                                if (String(current) !== v) setBudget(inst, v);
                              }}
                            />
                            <small>Hard-stops AI calls at 100%; warn at 80%</small>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {visible.length === 0 && (
          <div className="ffp-loading">{q ? 'No institutions match your search.' : 'No institutions found.'}</div>
        )}
      </div>
    </div>
  );
};

export default InstitutionFeaturePanel;
